import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles/institution.css";
import { AppShell } from "../components/AppShell";
import { LineField } from "../components/LineField";
import { EmptyState } from "../components/EmptyState";
import { PaginationBar, usePagination } from "../components/Pagination";
import { OfficerBranchSelect } from "../components/OfficerBranchSelect";
import { OrganisationInvitations } from "../components/OrganisationInvitations";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n/LanguageProvider";
import {
  listInstitutionOfficers,
  inviteInstitutionOfficer,
  disableInstitutionOfficer,
  promoteInstitutionOfficer,
  listOrganisationOfficers,
  inviteOrganisationOfficer,
  disableOrganisationOfficer,
  promoteOrganisationOfficer,
  listJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
  createOrgInvite,
  resendInstitutionOfficerInvite,
  resendOrganisationInvite,
  revokeOrganisationInvite,
  getInstitutionHiringQuota,
  initiateAdditionalOfficerPurchase,
} from "../api/officers";
import { handleCashfreeCheckout, verifyPayment } from "../api/payments";
import { fetchBranchesForAdmin } from "../api/organisations";

import { C, font } from "../styles/tokens";
import { InlineIcon } from "../components/InlineIcon";
import {
  officerAccountStatus,
  officerCanBeDisabled,
  officerIsActive,
} from "../utils/officerStatus";

const MAX_OFFICER_LIMIT_PER_TYPE = 2; // Maximum 2 Verification & 2 Recruitment officers

export function OfficersPage() {
  const isMobile = useIsMobile();
  const t = useT();
  const navigate = useNavigate();
  const { localUser } = useAuth();
  const isInstitution = localUser?.type === "INSTITUTION_OFFICER";
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";

  // API dispatch
  const api = isInstitution
    ? {
        list: listInstitutionOfficers,
        invite: inviteInstitutionOfficer,
        disable: disableInstitutionOfficer,
        promote: promoteInstitutionOfficer,
      }
    : {
        list: listOrganisationOfficers,
        invite: inviteOrganisationOfficer,
        disable: disableOrganisationOfficer,
        promote: promoteOrganisationOfficer,
      };

  const myOfficerRecord = isInstitution
    ? localUser?.institutionOfficer
    : localUser?.organisationOfficer;
  const iAmAdmin =
    myOfficerRecord?.role === "PRIMARY_ADMIN" ||
    myOfficerRecord?.role === "ADMIN";
  const myOfficerId = myOfficerRecord?.id;
  const orgId = myOfficerRecord?.organisationId;

  const [officers, setOfficers] = useState([]);
  const [hiringQuota, setHiringQuota] = useState(null);
  const location = useLocation();
  const VALID_TABS = ["VERIFICATION", "RECRUITMENT", "JOIN_REQUESTS"];
  const [activeTab, setActiveTab] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam && VALID_TABS.includes(tabParam)) {
      return tabParam;
    }
    return "VERIFICATION";
  }); // VERIFICATION | RECRUITMENT | JOIN_REQUESTS

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam && VALID_TABS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Join Requests State (Organisations)
  const [joinRequests, setJoinRequests] = useState([]);
  const [resolvingRequestId, setResolvingRequestId] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [targetRejectRequest, setTargetRejectRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Invite Modal State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    officerType: "VERIFICATION", // VERIFICATION or RECRUITMENT
    fullName: "",
    email: "",
    phone: "",
    employeeId: "",
    designation: "Verification Officer",
    termsAccepted: false,
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // The organisation's branches, for an admin setting an officer's branch.
  const [branches, setBranches] = useState(null);

  // Changes when an invitation is sent, so the waiting list reads it.
  const [invitesVersion, setInvitesVersion] = useState(0);

  // Action states
  const [actingId, setActingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const data = await api.list();
      setOfficers(data);

      if (isInstitution || isOrganisation) {
        try {
          const quota = await getInstitutionHiringQuota(isOrganisation);
          setHiringQuota(quota);
        } catch (e) {
          console.error("Failed to load hiring quota:", e);
        }
      }

      if (isOrganisation && iAmAdmin && orgId) {
        try {
          const reqs = await listJoinRequests(orgId);
          setJoinRequests(reqs || []);
        } catch (e) {
          console.error("Failed to load join requests:", e);
        }
        try {
          setBranches(await fetchBranchesForAdmin(orgId));
        } catch (e) {
          console.error("Failed to load branches:", e);
        }
      }
      setError(null);
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  }

  async function handleApproveJoin(requestId) {
    if (!orgId) return;
    setResolvingRequestId(requestId);
    try {
      await approveJoinRequest(orgId, requestId);
      alert(
        "Join request approved! An account setup invitation link has been dispatched to the officer.",
      );
      await reload();
    } catch (err) {
      alert(err?.message ?? "Failed to approve join request");
    } finally {
      setResolvingRequestId(null);
    }
  }

  async function handleConfirmReject() {
    if (!orgId || !targetRejectRequest) return;
    setResolvingRequestId(targetRejectRequest.id);
    try {
      await rejectJoinRequest(
        orgId,
        targetRejectRequest.id,
        rejectionReason.trim() || undefined,
      );
      setRejectModalOpen(false);
      setTargetRejectRequest(null);
      setRejectionReason("");
      alert("Join request rejected.");
      await reload();
    } catch (err) {
      alert(err?.message ?? "Failed to reject join request");
    } finally {
      setResolvingRequestId(null);
    }
  }

  useEffect(() => {
    if (isInstitution || isOrganisation) reload();
    else setLoading(false);
  }, []);

  function handleOpenInviteModal(defaultType = "VERIFICATION") {
    setInviteForm({
      officerType: defaultType,
      fullName: "",
      email: "",
      phone: "",
      employeeId: "",
      designation:
        defaultType === "RECRUITMENT"
          ? "Recruitment Officer"
          : "Verification Officer",
      termsAccepted: false,
    });
    setInviteError(null);
    setShowInviteModal(true);
  }

  const maxRecruiterLimit =
    isInstitution || isOrganisation
      ? (hiringQuota?.totalPermittedQuota ?? 2)
      : MAX_OFFICER_LIMIT_PER_TYPE;
  const maxVerifierLimit =
    isInstitution || isOrganisation
      ? (hiringQuota?.totalPermittedVerifierQuota ?? 2)
      : MAX_OFFICER_LIMIT_PER_TYPE;

  // Filter officers based on selected tab and explicit officerType
  const verifiersList = officers.filter((o) => {
    if (o.officerType) {
      const t = o.officerType.toUpperCase();
      if (t === "HIRING" || t === "RECRUITMENT") return false;
      if (t === "VERIFICATION") return true;
    }
    const des = (o.designation || "").toLowerCase();
    return (
      !des.includes("recruitment") &&
      !des.includes("recruiter") &&
      !des.includes("hiring")
    );
  });

  const recruitersList = officers.filter((o) => {
    if (o.officerType) {
      const t = o.officerType.toUpperCase();
      if (t === "HIRING" || t === "RECRUITMENT") return true;
      if (t === "VERIFICATION") return false;
    }
    const des = (o.designation || "").toLowerCase();
    return (
      des.includes("recruitment") ||
      des.includes("recruiter") ||
      des.includes("hiring")
    );
  });

  async function handleInviteSubmit(e) {
    e.preventDefault();
    setInviting(true);
    setInviteError(null);

    const type = inviteForm.officerType;
    const isHiring = type === "RECRUITMENT";
    const currentCount = isHiring
      ? recruitersList.length
      : verifiersList.length;
    const currentLimit = isHiring ? maxRecruiterLimit : maxVerifierLimit;
    const isPaidOfficer =
      (isInstitution || isOrganisation) && currentCount >= currentLimit;

    // Limit Validation Guard
    if (currentCount >= currentLimit && !isPaidOfficer) {
      setInviteError(
        `Limit reached: Only ${currentLimit} ${
          isHiring ? "Recruitment" : "Verification"
        } Officers are allowed. Please upgrade your subscription plan.`,
      );
      setInviting(false);
      return;
    }

    try {
      const finalDesignation =
        inviteForm.designation.trim() ||
        (isHiring ? "Recruitment Officer" : "Verification Officer");

      if (isPaidOfficer) {
        if (!inviteForm.termsAccepted) {
          setInviteError(
            "Please accept the Terms & Conditions and Privacy Policy before continuing with payment.",
          );
          setInviting(false);
          return;
        }

        // Paid Additional Officer Access via Cashfree
        const res = await initiateAdditionalOfficerPurchase(
          {
            fullName: inviteForm.fullName.trim(),
            email: inviteForm.email.trim().toLowerCase(),
            phone: inviteForm.phone.trim() || undefined,
            employeeId: inviteForm.employeeId.trim(),
            designation: finalDesignation,
            officerType: isHiring ? "HIRING" : "VERIFICATION",
            termsAccepted: true,
          },
          isOrganisation,
        );

        const order = res.order;
        if (order?.bypass) {
          await verifyPayment({
            provider: "CASHFREE",
            orderId: order.orderId,
            paymentId: "bypass-no-payment",
            signature: "bypass-no-signature",
          });
        } else {
          await handleCashfreeCheckout(order);
        }

        alert(
          `Additional ${isHiring ? "hiring" : "verification"} officer seat purchased and officer invited successfully!`,
        );
        setShowInviteModal(false);
        await reload();
        return;
      }

      if (isOrganisation && orgId) {
        await createOrgInvite(orgId, {
          email: inviteForm.email.trim().toLowerCase(),
          fullName: inviteForm.fullName.trim(),
          designation: finalDesignation,
          role: "MEMBER",
        });
        setInvitesVersion((version) => version + 1);
      } else {
        const invited = await api.invite({
          fullName: inviteForm.fullName.trim(),
          email: inviteForm.email.trim().toLowerCase(),
          phone: inviteForm.phone.trim() || undefined,
          employeeId: inviteForm.employeeId.trim(),
          designation: finalDesignation,
          officerType: isHiring ? "HIRING" : "VERIFICATION",
        });
        if (invited?.inviteEmailSent === false) {
          alert(
            "The officer was added, but the invitation email could not be sent. Use Resend Invite Link on their row to try again.",
          );
        }
      }

      setShowInviteModal(false);
      await reload();
    } catch (err) {
      setInviteError(err.message ?? t("common.somethingWrong"));
    } finally {
      setInviting(false);
    }
  }

  /** An officer's new branch shows at once. */
  function handleBranchChanged(updated) {
    setOfficers((list) =>
      list.map((item) =>
        item.id === updated.id
          ? { ...item, branchId: updated.branchId, branch: updated.branch }
          : item,
      ),
    );
  }

  async function handleDisable(officerId) {
    const officer = officers.find((o) => o.id === officerId);
    if (officer?.isInviteRecord) {
      if (
        !confirm(
          "Revoke this pending invitation? The invitation link will stop working.",
        )
      )
        return;
      setActingId(officerId);
      setOpenMenuId(null);
      try {
        await revokeOrganisationInvite(orgId, officer.inviteId || officer.id);
        await reload();
      } catch (err) {
        alert(err.message ?? "Failed to revoke invitation");
      } finally {
        setActingId(null);
      }
      return;
    }

    const question =
      officerAccountStatus(officer) === "DELETED"
        ? "This officer deleted their account. Disable it for good? They will not be able to reactivate it or come back to your team."
        : "Disable this officer? They will lose access immediately.";
    if (!confirm(question)) return;
    setActingId(officerId);
    setOpenMenuId(null);
    try {
      await api.disable(officerId);
      await reload();
    } catch (err) {
      alert(err.message ?? "Failed to disable officer");
    } finally {
      setActingId(null);
    }
  }

  async function handlePromote(officerId) {
    if (
      !confirm(
        "Promote this officer to admin? They will be able to invite and manage other officers.",
      )
    )
      return;
    setActingId(officerId);
    setOpenMenuId(null);
    try {
      await api.promote(officerId);
      await reload();
    } catch (err) {
      alert(err.message ?? "Failed to promote officer");
    } finally {
      setActingId(null);
    }
  }

  // Pending officers who have not accepted yet get a fresh
  // 72-hour link by email.
  async function handleResendInvite(officer) {
    setOpenMenuId(null);
    setActingId(officer.id);
    try {
      if (isOrganisation && orgId) {
        await resendOrganisationInvite(orgId, officer.inviteId || officer.id);
      } else {
        await resendInstitutionOfficerInvite(officer.id);
      }
      alert(
        `A new invitation link has been emailed to ${officer.user?.email}. It stays valid for 72 hours.`,
      );
    } catch (err) {
      alert(err?.message ?? "Couldn't send the invitation. Please try again.");
    } finally {
      setActingId(null);
    }
  }

  // Pending institution or organisation invite has a link to resend.
  const canResendInvite = (officer) =>
    (isInstitution || isOrganisation) &&
    officerAccountStatus(officer) === "PENDING";

  // Active officer and tab list views
  const currentList =
    activeTab === "VERIFICATION" ? verifiersList : recruitersList;
  const pager = usePagination(currentList, 10);
  const activeCount = officers.filter((o) => officerIsActive(o)).length;
  const pendingJoinCount = joinRequests.filter(
    (r) => r.status === "PENDING",
  ).length;

  if (!isInstitution && !isOrganisation) {
    return (
      <AppShell>
        <div
          style={{
            background: "#ffffff",
            padding: 24,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}
        >
          This page is for institution and organisation officers.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth="100%">
      <div style={{ width: "100%", paddingBottom: 40 }}>
        {/* Header Breadcrumb & Titles */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 13,
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <span
              onClick={() => navigate("/dashboard")}
              style={{ cursor: "pointer", color: "#64748b" }}
            >
              Home
            </span>
            <span style={{ color: "#cbd5e1" }}>/</span>
            <span style={{ color: "#0f172a", fontWeight: 600 }}>Officers</span>
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#2563eb",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            PEOPLE & PERMISSIONS
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "flex-end",
              gap: isMobile ? 12 : 0,
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: 800,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                Officers
              </h1>
              <p
                style={{
                  fontSize: isMobile ? 13 : 14,
                  color: "#64748b",
                  margin: "4px 0 0",
                }}
              >
                {isInstitution || isOrganisation
                  ? `Manage the team responsible for applications and recruitment. (Default 2 Verification & 2 Hiring officers allowed. Current quotas: ${maxVerifierLimit} Verification, ${maxRecruiterLimit} Hiring)`
                  : "Manage the team responsible for applications and recruitment. (Max 2 Verification & 2 Recruitment officers allowed on free plan)"}
              </p>
            </div>

            {iAmAdmin && (
              <button
                onClick={() =>
                  handleOpenInviteModal(
                    activeTab === "JOIN_REQUESTS" ? "VERIFICATION" : activeTab,
                  )
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "10px 20px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "#2563eb",
                  border: "none",
                  borderRadius: 10,
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  width: isMobile ? "100%" : "auto",
                  minHeight: isMobile ? 44 : "auto",
                }}
              >
                + Invite officer
              </button>
            )}
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "#fef2f2",
              color: "#dc2626",
              padding: 14,
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Main Card Container */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            overflow: "hidden",
          }}
        >
          {/* Card Header */}
          <div
            style={{
              padding: isMobile ? "16px 18px" : "24px 28px",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: isMobile ? 12 : 0,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: isMobile ? 18 : 20,
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Officer directory
              </h2>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                Select an officer type to view its assigned team.
              </div>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#15803d",
                background: "#dcfce7",
                padding: "6px 14px",
                borderRadius: 20,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#16a34a",
                }}
              />
              {activeCount} active
            </div>
          </div>

          {/* Filter Tabs Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 16 : 28,
              padding: isMobile ? "0 16px" : "0 28px",
              borderBottom: "1px solid #e2e8f0",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              whiteSpace: "nowrap",
            }}
          >
            <button
              onClick={() => setActiveTab("VERIFICATION")}
              style={{
                background: "none",
                border: "none",
                padding: "10px 0 14px",
                fontSize: 14,
                fontWeight: activeTab === "VERIFICATION" ? 700 : 500,
                color: activeTab === "VERIFICATION" ? "#2563eb" : "#64748b",
                borderBottom:
                  activeTab === "VERIFICATION"
                    ? "2px solid #2563eb"
                    : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <span>Verification Officer</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color:
                    verifiersList.length >= maxVerifierLimit
                      ? "#b45309"
                      : activeTab === "VERIFICATION"
                        ? "#2563eb"
                        : "#64748b",
                  background:
                    verifiersList.length >= maxVerifierLimit
                      ? "#fef3c7"
                      : activeTab === "VERIFICATION"
                        ? "#eff6ff"
                        : "#f1f5f9",
                  padding: "2px 8px",
                  borderRadius: 12,
                }}
              >
                {verifiersList.length} / {maxVerifierLimit}
              </span>
            </button>

            {/* Recruitment Officer tab hidden/commented per request
            <button
              onClick={() => setActiveTab("RECRUITMENT")}
              style={{
                background: "none",
                border: "none",
                padding: "10px 0 14px",
                fontSize: 14,
                fontWeight: activeTab === "RECRUITMENT" ? 700 : 500,
                color: activeTab === "RECRUITMENT" ? "#2563eb" : "#64748b",
                borderBottom:
                  activeTab === "RECRUITMENT"
                    ? "2px solid #2563eb"
                    : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <span>Recruitment Officer</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color:
                    recruitersList.length >= maxRecruiterLimit
                      ? "#b45309"
                      : activeTab === "RECRUITMENT"
                        ? "#2563eb"
                        : "#64748b",
                  background:
                    recruitersList.length >= maxRecruiterLimit
                      ? "#fef3c7"
                      : activeTab === "RECRUITMENT"
                        ? "#eff6ff"
                        : "#f1f5f9",
                  padding: "2px 8px",
                  borderRadius: 12,
                }}
              >
                {recruitersList.length} / {maxRecruiterLimit}
              </span>
            </button>
            */}

            {isOrganisation && iAmAdmin && (
              <button
                onClick={() => setActiveTab("JOIN_REQUESTS")}
                style={{
                  background: "none",
                  border: "none",
                  padding: "10px 0 14px",
                  fontSize: 14,
                  fontWeight: activeTab === "JOIN_REQUESTS" ? 700 : 500,
                  color: activeTab === "JOIN_REQUESTS" ? "#2563eb" : "#64748b",
                  borderBottom:
                    activeTab === "JOIN_REQUESTS"
                      ? "2px solid #2563eb"
                      : "2px solid transparent",
                  marginBottom: -1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <span>Join Requests</span>
                {pendingJoinCount > 0 ? (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#b45309",
                      background: "#fef3c7",
                      padding: "2px 8px",
                      borderRadius: 12,
                    }}
                  >
                    {pendingJoinCount} pending
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color:
                        activeTab === "JOIN_REQUESTS" ? "#2563eb" : "#64748b",
                      background:
                        activeTab === "JOIN_REQUESTS" ? "#eff6ff" : "#f1f5f9",
                      padding: "2px 8px",
                      borderRadius: 12,
                    }}
                  >
                    {joinRequests.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div
              style={{
                padding: "40px 0",
                textAlign: "center",
                fontSize: 14,
                color: "#64748b",
              }}
            >
              Loading real-time officer records...
            </div>
          ) : activeTab === "JOIN_REQUESTS" ? (
            joinRequests.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <EmptyState
                  kind="folder"
                  title="No Join Requests"
                  body="When colleagues request to join this organisation, their requests will appear here for your review."
                />
              </div>
            ) : isMobile ? (
              <div style={{ padding: "14px" }}>
                {joinRequests.map((req) => (
                  <div
                    key={req.id}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "14px",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: "#eff6ff",
                          color: "#2563eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 13,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(req.fullName)}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#0f172a",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {req.fullName}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#64748b",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {req.email}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color: "#334155",
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {req.designation || "Officer"}
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}
                      >
                        {req.mobile && <span>Mobile: {req.mobile}</span>}
                        {req.mobile && req.employeeId && (
                          <span aria-hidden="true"> | </span>
                        )}
                        {req.employeeId && <span>ID: {req.employeeId}</span>}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 12,
                      }}
                    >
                      {req.status === "PENDING" ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            borderRadius: 16,
                            background: "#fef3c7",
                            color: "#b45309",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "#f59e0b",
                            }}
                          />
                          Pending Review
                        </span>
                      ) : req.status === "APPROVED" ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            borderRadius: 16,
                            background: "#dcfce7",
                            color: "#15803d",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "#16a34a",
                            }}
                          />
                          Approved
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            borderRadius: 16,
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "#dc2626",
                            }}
                          />
                          Rejected
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>
                        {formatRelative(req.createdAt)}
                      </span>
                    </div>

                    {req.rejectionReason && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          background: "#f8fafc",
                          padding: "6px 10px",
                          borderRadius: 6,
                          marginBottom: 10,
                        }}
                      >
                        {req.rejectionReason}
                      </div>
                    )}

                    {req.status === "PENDING" ? (
                      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                        <button
                          onClick={() => handleApproveJoin(req.id)}
                          disabled={resolvingRequestId === req.id}
                          style={{
                            flex: 1,
                            minHeight: 44,
                            padding: "8px 14px",
                            borderRadius: 8,
                            border: "none",
                            background: "#16a34a",
                            color: "#ffffff",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor:
                              resolvingRequestId === req.id
                                ? "not-allowed"
                                : "pointer",
                            opacity: resolvingRequestId === req.id ? 0.6 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {resolvingRequestId === req.id
                            ? "Approving..."
                            : "Approve"}
                        </button>
                        <button
                          onClick={() => {
                            setTargetRejectRequest(req);
                            setRejectModalOpen(true);
                          }}
                          disabled={resolvingRequestId === req.id}
                          style={{
                            flex: 1,
                            minHeight: 44,
                            padding: "8px 14px",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                            background: "#ffffff",
                            color: "#dc2626",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor:
                              resolvingRequestId === req.id
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#94a3b8",
                          textAlign: "right",
                        }}
                      >
                        Resolved
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                    fontFamily: font,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <th style={thStyle}>APPLICANT</th>
                      <th style={thStyle}>ROLE & DETAILS</th>
                      <th style={thStyle}>STATUS</th>
                      <th style={thStyle}>REQUESTED</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {joinRequests.map((req) => (
                      <tr
                        key={req.id}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td style={tdStyle}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: "50%",
                                background: "#eff6ff",
                                color: "#2563eb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            >
                              {getInitials(req.fullName)}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#0f172a",
                                }}
                              >
                                {req.fullName}
                              </div>
                              <div style={{ fontSize: 12, color: "#64748b" }}>
                                {req.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500, color: "#334155" }}>
                            {req.designation || "Officer"}
                          </div>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            {req.mobile && <span>Mobile: {req.mobile}</span>}
                            {req.mobile && req.employeeId && (
                              <span aria-hidden="true"> | </span>
                            )}
                            {req.employeeId && (
                              <span>ID: {req.employeeId}</span>
                            )}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {req.status === "PENDING" ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 12px",
                                borderRadius: 16,
                                background: "#fef3c7",
                                color: "#b45309",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: "#f59e0b",
                                }}
                              />
                              Pending Review
                            </span>
                          ) : req.status === "APPROVED" ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 12px",
                                borderRadius: 16,
                                background: "#dcfce7",
                                color: "#15803d",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: "#16a34a",
                                }}
                              />
                              Approved
                            </span>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 12px",
                                borderRadius: 16,
                                background: "#fee2e2",
                                color: "#b91c1c",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: "#dc2626",
                                }}
                              />
                              Rejected
                            </span>
                          )}
                          {req.rejectionReason && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#94a3b8",
                                marginTop: 4,
                              }}
                            >
                              {req.rejectionReason}
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            {formatRelative(req.createdAt)}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {req.status === "PENDING" ? (
                            <div style={{ display: "inline-flex", gap: 8 }}>
                              <button
                                onClick={() => handleApproveJoin(req.id)}
                                disabled={resolvingRequestId === req.id}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  border: "none",
                                  background: "#16a34a",
                                  color: "#ffffff",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor:
                                    resolvingRequestId === req.id
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity:
                                    resolvingRequestId === req.id ? 0.6 : 1,
                                }}
                              >
                                {resolvingRequestId === req.id
                                  ? "Approving..."
                                  : "Approve"}
                              </button>
                              <button
                                onClick={() => {
                                  setTargetRejectRequest(req);
                                  setRejectModalOpen(true);
                                }}
                                disabled={resolvingRequestId === req.id}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  border: "1px solid #e2e8f0",
                                  background: "#ffffff",
                                  color: "#dc2626",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor:
                                    resolvingRequestId === req.id
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>
                              Resolved
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : currentList.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <EmptyState
                kind="folder"
                title={`No ${activeTab === "VERIFICATION" ? "Verification" : "Recruitment"} Officers`}
                body={`Click "+ Invite officer" to add a new ${activeTab === "VERIFICATION" ? "Verification" : "Recruitment"} Officer.`}
              />
            </div>
          ) : isMobile ? (
            <div style={{ padding: "14px" }}>
              {pager.pageItems.map((officer) => {
                const fullName = officer.fullName || "Officer";
                const initials = getInitials(fullName);
                const email = officer.user?.email || "No email on record";
                const isAdmin = isAdminRole(officer.role);
                const roleLabel = roleLabelFor(officer);
                const isMe = officer.id === myOfficerId;
                const activityText = formatLastActivity(officer);
                // The founding primary admin cannot be changed by others.
                // Someone who deleted their account can only be disabled.
                const showActions =
                  iAmAdmin &&
                  !isMe &&
                  officerCanBeDisabled(officer) &&
                  officer.role !== "PRIMARY_ADMIN";

                return (
                  <div
                    key={officer.id}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "14px",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: "#f1f5f9",
                          color: "#475569",
                          fontSize: 13,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#0f172a",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {fullName}
                          </span>
                          {isMe && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "2px 8px",
                                borderRadius: 12,
                                background: "#dcfce7",
                                color: "#15803d",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              <span
                                style={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  background: "#16a34a",
                                }}
                              />
                              You
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#64748b",
                            marginTop: 2,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {email}
                        </div>
                        {isOrganisation && (
                          <div className="mt-1">
                            <OfficerBranchSelect
                              officer={officer}
                              branches={branches}
                              canChange={
                                iAmAdmin && officerCanBeDisabled(officer)
                              }
                              onChanged={handleBranchChanged}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "3px 10px",
                          borderRadius: 16,
                          background: "#f1f5f9",
                          color: "#334155",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {roleLabel}
                      </span>
                      <OfficerStatusPill officer={officer} compact />
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748b",
                        marginBottom: showActions ? 10 : 0,
                      }}
                    >
                      {activityText}
                    </div>

                    {showActions && (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: "1px dashed #e2e8f0",
                        }}
                      >
                        {!isAdmin &&
                          officerIsActive(officer) &&
                          !officer.isInviteRecord && (
                            <button
                              onClick={() => handlePromote(officer.id)}
                              disabled={actingId === officer.id}
                              style={{
                                flex: 1,
                                minHeight: 40,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#15803d",
                                background: "#dcfce7",
                                border: "none",
                                borderRadius: 6,
                                cursor:
                                  actingId === officer.id
                                    ? "not-allowed"
                                    : "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              Promote to Admin
                            </button>
                          )}
                        {canResendInvite(officer) && (
                          <button
                            onClick={() => handleResendInvite(officer)}
                            disabled={actingId === officer.id}
                            style={{
                              flex: 1,
                              minHeight: 40,
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#2563eb",
                              background: "#eff6ff",
                              border: "none",
                              borderRadius: 6,
                              cursor:
                                actingId === officer.id
                                  ? "not-allowed"
                                  : "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            Resend Link
                          </button>
                        )}
                        <button
                          onClick={() => handleDisable(officer.id)}
                          disabled={actingId === officer.id}
                          style={{
                            flex: 1,
                            minHeight: 40,
                            padding: "6px 10px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#dc2626",
                            background: "#fee2e2",
                            border: "none",
                            borderRadius: 6,
                            cursor:
                              actingId === officer.id
                                ? "not-allowed"
                                : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {officer.isInviteRecord ? "Revoke" : "Disable"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{ padding: "8px 0" }}>
                <PaginationBar pager={pager} itemLabel="officers" />
              </div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  fontFamily: font,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th style={thStyle}>OFFICER</th>
                    <th style={thStyle}>ROLE</th>
                    <th style={thStyle}>STATUS</th>
                    <th style={thStyle}>LAST ACTIVITY</th>
                    <th style={{ ...thStyle, textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageItems.map((officer) => (
                    <OfficerTableRow
                      key={officer.id}
                      officer={officer}
                      isMe={officer.id === myOfficerId}
                      iAmAdmin={iAmAdmin}
                      acting={actingId === officer.id}
                      menuOpen={openMenuId === officer.id}
                      onToggleMenu={() =>
                        setOpenMenuId(
                          openMenuId === officer.id ? null : officer.id,
                        )
                      }
                      onDisable={() => handleDisable(officer.id)}
                      onPromote={() => handlePromote(officer.id)}
                      onResendInvite={
                        canResendInvite(officer)
                          ? () => handleResendInvite(officer)
                          : undefined
                      }
                      branchControl={
                        isOrganisation ? (
                          <OfficerBranchSelect
                            officer={officer}
                            branches={branches}
                            canChange={
                              iAmAdmin && officerCanBeDisabled(officer)
                            }
                            onChanged={handleBranchChanged}
                          />
                        ) : null
                      }
                    />
                  ))}
                </tbody>
              </table>

              <div style={{ padding: "12px 24px" }}>
                <PaginationBar pager={pager} itemLabel="officers" />
              </div>
            </div>
          )}
        </div>

        {isOrganisation && iAmAdmin && orgId && (
          <OrganisationInvitations
            organisationId={orgId}
            refreshKey={invitesVersion}
          />
        )}
      </div>

      {/* Invite Officer Modal */}
      {showInviteModal && (
        <InviteOfficerModal
          form={inviteForm}
          setForm={setInviteForm}
          inviting={inviting}
          error={inviteError}
          isInstitution={isInstitution}
          isOrganisation={isOrganisation}
          verifierCount={verifiersList.length}
          recruiterCount={recruitersList.length}
          maxRecruiterLimit={maxRecruiterLimit}
          maxVerifierLimit={maxVerifierLimit}
          additionalOfficerPrice={
            hiringQuota?.additionalOfficerPrice ??
            hiringQuota?.priceInRupees ??
            (hiringQuota?.pricePaise
              ? Math.round(hiringQuota.pricePaise / 100)
              : 1200)
          }
          onNavigateBilling={() => {
            setShowInviteModal(false);
            navigate("/billing");
          }}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteSubmit}
        />
      )}

      {/* Reject Join Request Modal */}
      {rejectModalOpen && targetRejectRequest && (
        <RejectJoinRequestModal
          target={targetRejectRequest}
          reason={rejectionReason}
          setReason={setRejectionReason}
          resolving={resolvingRequestId === targetRejectRequest.id}
          onClose={() => {
            setRejectModalOpen(false);
            setTargetRejectRequest(null);
            setRejectionReason("");
          }}
          onConfirm={handleConfirmReject}
        />
      )}
    </AppShell>
  );
}

/* =============================================================================
   InviteOfficerModal Component with Upgrade Requirement Prompt
   ============================================================================= */

function InviteOfficerModal({
  form,
  setForm,
  inviting,
  error,
  isInstitution,
  isOrganisation,
  verifierCount,
  recruiterCount,
  maxRecruiterLimit = 2,
  maxVerifierLimit = 2,
  additionalOfficerPrice = 1200,
  onNavigateBilling,
  onClose,
  onSubmit,
}) {
  const isMobile = useIsMobile();
  const priceNumber = Number(additionalOfficerPrice) || 1200;
  const formattedPrice = `₹${priceNumber.toLocaleString("en-IN")}`;
  const handleTypeChange = (type) => {
    setForm((f) => ({
      ...f,
      officerType: type,
      designation:
        type === "RECRUITMENT" ? "Recruitment Officer" : "Verification Officer",
    }));
  };

  const isRecruiter = form.officerType === "RECRUITMENT";
  const selectedCount = isRecruiter ? recruiterCount : verifierCount;
  const currentLimit = isRecruiter ? maxRecruiterLimit : maxVerifierLimit;
  const isLimitReached = selectedCount >= currentLimit;
  const isPaidOfficerAction =
    (isInstitution || isOrganisation) && isLimitReached;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "12px 10px" : "20px 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 620,
          maxHeight: "min(92vh, 780px)",
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 25px 60px -15px rgba(15, 23, 42, 0.3)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        {/* Fixed Header */}
        <div
          style={{
            flexShrink: 0,
            padding: isMobile ? "14px 18px" : "18px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#ffffff",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
              }}
            >
              Invite Officer
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
              Select officer role and enter contact details.
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: "#94a3b8",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        {/* Form with Scrollable Content & Fixed Footer */}
        <form
          onSubmit={onSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Scrollable Form Body */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "16px 14px" : "22px 24px",
            }}
          >
            {/* Officer Type Selector */}
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                Select Officer Type
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => handleTypeChange("VERIFICATION")}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: 14,
                    borderRadius: 12,
                    border:
                      form.officerType === "VERIFICATION"
                        ? "2px solid #2563eb"
                        : "1px solid #e2e8f0",
                    background:
                      form.officerType === "VERIFICATION"
                        ? "#eff6ff"
                        : "#ffffff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <input
                    type="radio"
                    name="officerType"
                    value="VERIFICATION"
                    checked={form.officerType === "VERIFICATION"}
                    onChange={() => handleTypeChange("VERIFICATION")}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color:
                          form.officerType === "VERIFICATION"
                            ? "#1e40af"
                            : "#0f172a",
                      }}
                    >
                      Verification Officer
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}
                    >
                      Approves application submissions
                    </div>
                  </div>
                </button>

                {/* Recruitment Officer option hidden/commented per request
                <button
                  type="button"
                  onClick={() => handleTypeChange("RECRUITMENT")}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: 14,
                    borderRadius: 12,
                    border:
                      form.officerType === "RECRUITMENT"
                        ? "2px solid #2563eb"
                        : "1px solid #e2e8f0",
                    background:
                      form.officerType === "RECRUITMENT"
                        ? "#eff6ff"
                        : "#ffffff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <input
                    type="radio"
                    name="officerType"
                    value="RECRUITMENT"
                    checked={form.officerType === "RECRUITMENT"}
                    onChange={() => handleTypeChange("RECRUITMENT")}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color:
                          form.officerType === "RECRUITMENT"
                            ? "#1e40af"
                            : "#0f172a",
                      }}
                    >
                      Recruitment Officer
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}
                    >
                      Manages hiring & applications
                    </div>
                  </div>
                </button>
                */}
              </div>
            </div>

            {/* Upgrade Banner or Paid Additional Officer Notice */}
            {isPaidOfficerAction ? (
              <div
                style={{
                  background: "#fef3c7",
                  border: "1.5px solid #f59e0b",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#92400e",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <InlineIcon name="warning" size={16} />{" "}
                  {isRecruiter
                    ? `Default Hiring Officer Limit Reached (${recruiterCount}/${maxRecruiterLimit})`
                    : `Default Verification Officer Limit Reached (${verifierCount}/${maxVerifierLimit})`}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#78350f",
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  {isRecruiter
                    ? "You have reached the default limit of two hiring officers. To add another hiring officer, please complete the additional officer payment of "
                    : "You have reached the default limit of two verification officers. To add another verification officer, please complete the additional officer payment of "}
                  <strong>{formattedPrice}</strong>.
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#16a34a",
                    background: "#dcfce7",
                    padding: "4px 8px",
                    borderRadius: 6,
                    display: "inline-block",
                  }}
                >
                  {formattedPrice} fee • Verified securely via Cashfree
                </div>
              </div>
            ) : isLimitReached ? (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1.5px solid #fcd34d",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#b45309",
                    marginBottom: 4,
                  }}
                >
                  <InlineIcon name="warning" size={14} /> Maximum Officer Limit
                  Reached ({selectedCount}/{currentLimit})
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#92400e",
                    lineHeight: 1.5,
                    marginBottom: 12,
                  }}
                >
                  You have reached the maximum allowed limit of{" "}
                  <strong>
                    {currentLimit}{" "}
                    {form.officerType === "RECRUITMENT"
                      ? "Recruitment Officers"
                      : "Verification Officers"}
                  </strong>{" "}
                  on your current plan. To invite additional officers, please
                  upgrade your subscription plan.
                </div>
                <button
                  type="button"
                  onClick={onNavigateBilling}
                  style={{
                    padding: "9px 18px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#ffffff",
                    background: "#d97706",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    width: "100%",
                    minHeight: isMobile ? 44 : "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Upgrade Plan to Add More Officers
                </button>
              </div>
            ) : null}

            {/* Form Fields: show if under limit OR if paid officer action */}
            {(!isLimitReached || isPaidOfficerAction) && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <LineField
                  label="Full Name"
                  placeholder="e.g. Dr. Priya Menon"
                  value={form.fullName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, fullName: e.target.value }))
                  }
                  required
                />

                <LineField
                  label="Email Address"
                  type="email"
                  placeholder="officer@yourinstitution.edu"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <LineField
                    label="Employee ID"
                    placeholder="EMP-1024"
                    value={form.employeeId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, employeeId: e.target.value }))
                    }
                    required
                  />

                  <LineField
                    label="Designation / Role"
                    placeholder={
                      form.officerType === "RECRUITMENT"
                        ? "Recruitment Officer"
                        : "Verification Officer"
                    }
                    value={form.designation}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, designation: e.target.value }))
                    }
                    required
                  />
                </div>
              </div>
            )}

            {/* Terms Acceptance Checkbox for Paid Additional Officer */}
            {isPaidOfficerAction && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: form.termsAccepted ? "#f0fdf4" : "#f8fafc",
                  border: `1.5px solid ${form.termsAccepted ? "#16a34a" : "#cbd5e1"}`,
                  borderRadius: 10,
                  transition: "all 0.2s ease",
                }}
              >
                <label
                  htmlFor="paid-officer-terms-checkbox"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    cursor: "pointer",
                    fontSize: 13,
                    color: "#334155",
                    lineHeight: 1.5,
                    userSelect: "none",
                  }}
                >
                  <input
                    id="paid-officer-terms-checkbox"
                    type="checkbox"
                    checked={!!form.termsAccepted}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        termsAccepted: e.target.checked,
                      }))
                    }
                    style={{
                      marginTop: 2,
                      width: 16,
                      height: 16,
                      accentColor: "#16a34a",
                      cursor: "pointer",
                    }}
                  />
                  <span>
                    I agree to the{" "}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2563eb",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2563eb",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
              </div>
            )}

            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  color: "#dc2626",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  marginTop: 16,
                }}
              >
                {error}
              </div>
            )}

            {/* End of Scrollable Body */}
          </div>

          {/* Fixed Footer Actions */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "flex-end",
              gap: 12,
              padding: isMobile ? "12px 16px" : "16px 24px",
              borderTop: "1px solid #f1f5f9",
              background: "#ffffff",
            }}
          >
            {(!isLimitReached || isPaidOfficerAction) && (
              <button
                type="submit"
                disabled={
                  inviting || (isPaidOfficerAction && !form.termsAccepted)
                }
                style={{
                  order: isMobile ? 1 : 2,
                  width: isMobile ? "100%" : "auto",
                  minHeight: isMobile ? 44 : "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "9px 22px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: isPaidOfficerAction
                    ? form.termsAccepted
                      ? "#16a34a"
                      : "#94a3b8"
                    : "#2563eb",
                  border: "none",
                  borderRadius: 8,
                  cursor:
                    isPaidOfficerAction && !form.termsAccepted
                      ? "not-allowed"
                      : "pointer",
                  boxShadow:
                    isPaidOfficerAction && form.termsAccepted
                      ? "0 4px 12px rgba(22, 163, 74, 0.25)"
                      : isPaidOfficerAction
                        ? "none"
                        : "0 4px 12px rgba(37, 99, 235, 0.2)",
                }}
              >
                {inviting
                  ? isPaidOfficerAction
                    ? "Processing Payment..."
                    : "Sending invite..."
                  : isPaidOfficerAction
                    ? isRecruiter
                      ? `Pay and Add Hiring Officer (${formattedPrice})`
                      : `Pay and Add Verification Officer (${formattedPrice})`
                    : `Invite ${form.officerType === "RECRUITMENT" ? "Recruitment" : "Verification"} Officer`}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                order: isMobile ? 2 : 1,
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? 44 : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* =============================================================================
   OfficerTableRow Component
   ============================================================================= */

function OfficerTableRow({
  officer,
  isMe,
  iAmAdmin,
  acting,
  menuOpen,
  onToggleMenu,
  onDisable,
  onPromote,
  onResendInvite,
  branchControl,
}) {
  const fullName = officer.fullName || "Officer";
  const initials = getInitials(fullName);
  const email = officer.user?.email || "No email on record";
  const isAdmin = isAdminRole(officer.role);
  const roleLabel = roleLabelFor(officer);
  // The founding primary admin cannot be changed by others.
  // Someone who deleted their account can only be disabled.
  const showActions =
    iAmAdmin &&
    !isMe &&
    officerCanBeDisabled(officer) &&
    officer.role !== "PRIMARY_ADMIN";

  const activityText = formatLastActivity(officer);

  return (
    <tr
      style={{
        borderBottom: "1px solid #f1f5f9",
        transition: "background 0.15s ease",
      }}
    >
      {/* Officer Avatar & Name */}
      <td style={tdStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "#f1f5f9",
              color: "#475569",
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                {fullName}
              </span>
              {isMe && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    borderRadius: 12,
                    background: "#dcfce7",
                    color: "#15803d",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "#16a34a",
                    }}
                  />
                  You
                </span>
              )}
            </div>

            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {email}
            </div>
            {branchControl && <div className="mt-1">{branchControl}</div>}
          </div>
        </div>
      </td>

      {/* Role Pill */}
      <td style={tdStyle}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 12px",
            borderRadius: 16,
            background: "#f1f5f9",
            color: "#334155",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {roleLabel}
        </span>
      </td>

      {/* Status Pill */}
      <td style={tdStyle}>
        <OfficerStatusPill officer={officer} />
      </td>

      {/* Last Activity */}
      <td style={tdStyle}>
        <span style={{ fontSize: 13, color: "#64748b" }}>{activityText}</span>
      </td>

      {/* Actions */}
      <td style={{ ...tdStyle, textAlign: "right", position: "relative" }}>
        {showActions ? (
          <div>
            <button
              onClick={onToggleMenu}
              aria-label={`Actions for ${fullName}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{
                background: "none",
                border: "none",
                display: "inline-flex",
                alignItems: "center",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 4,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="5" cy="12" r="1.8"></circle>
                <circle cx="12" cy="12" r="1.8"></circle>
                <circle cx="19" cy="12" r="1.8"></circle>
              </svg>
            </button>

            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 12,
                  top: 40,
                  zIndex: 50,
                  background: "#ffffff",
                  borderRadius: 10,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                  border: "1px solid #e2e8f0",
                  padding: "6px 0",
                  minWidth: 160,
                  textAlign: "left",
                }}
              >
                {!isAdmin &&
                  officerIsActive(officer) &&
                  !officer.isInviteRecord && (
                    <button
                      onClick={onPromote}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#15803d",
                        background: "none",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      Promote to Admin
                    </button>
                  )}
                {onResendInvite && (
                  <button
                    onClick={onResendInvite}
                    disabled={acting}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#2563eb",
                      background: "none",
                      border: "none",
                      textAlign: "left",
                      cursor: acting ? "not-allowed" : "pointer",
                    }}
                  >
                    Resend Invite Link
                  </button>
                )}
                <button
                  onClick={onDisable}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "8px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#dc2626",
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  {officer.isInviteRecord ? "Revoke Invite" : "Disable Officer"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#cbd5e1" }}>---</span>
        )}
      </td>
    </tr>
  );
}

// Helpers
function isAdminRole(role) {
  return role === "PRIMARY_ADMIN" || role === "ADMIN";
}

// PRIMARY_ADMIN is the organisation's founding registrant.
function roleLabelFor(officer) {
  if (officer.role === "PRIMARY_ADMIN") return "Primary admin";
  if (officer.role === "ADMIN") return "Admin";
  return officer.designation || "Reviewer";
}

const STATUS_PILLS = {
  DELETED: {
    label: "Account deleted",
    color: C.textMed,
    dot: C.textLight,
    background: C.surface,
  },
  DISABLED: {
    label: "Disabled",
    color: "#dc2626",
    dot: "#dc2626",
    background: "#fef2f2",
  },
  PENDING: {
    label: "Invite pending",
    color: C.amber,
    dot: C.amber,
    background: C.amberLight,
  },
  ACTIVE: {
    label: "Active",
    color: "#15803d",
    dot: "#16a34a",
    background: "#dcfce7",
  },
};

function OfficerStatusPill({ officer, compact = false }) {
  const pill = STATUS_PILLS[officerAccountStatus(officer)];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compact ? "3px 10px" : "4px 12px",
        borderRadius: 16,
        background: pill.background,
        color: pill.color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: pill.dot,
        }}
      />
      {pill.label}
    </span>
  );
}

function getInitials(name) {
  if (!name) return "OF";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatLastActivity(officer) {
  if (officer.isInviteRecord || officerAccountStatus(officer) === "PENDING") {
    return "Invitation pending acceptance";
  }
  if ((officer.verificationsApproved ?? 0) > 0) {
    return `Approved an application today`;
  }
  if ((officer.verificationsRejected ?? 0) > 0) {
    return `Rejected an application today`;
  }
  if (officer.user?.lastLoginAt) {
    return `Last seen ${formatRelative(officer.user.lastLoginAt)}`;
  }
  return "Last seen 4m ago";
}

function formatRelative(iso) {
  if (!iso) return "recently";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const thStyle = {
  padding: "14px 24px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "16px 24px",
  verticalAlign: "middle",
};

function RejectJoinRequestModal({
  target,
  reason,
  setReason,
  resolving,
  onClose,
  onConfirm,
}) {
  const isMobile = useIsMobile();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 12 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: isMobile ? "16px 18px" : "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              margin: 0,
            }}
          >
            Reject Join Request
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: "#94a3b8",
              cursor: "pointer",
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        <div style={{ padding: isMobile ? "16px 14px" : 24 }}>
          <p
            style={{
              fontSize: 14,
              color: "#334155",
              margin: "0 0 16px",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            Are you sure you want to reject the join request from{" "}
            <strong>{target?.fullName}</strong> ({target?.email})?
          </p>

          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#475569",
              marginBottom: 6,
            }}
          >
            Reason for rejection (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Employee record not found in HR system"
            rows={3}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              fontSize: 14,
              fontFamily: font,
              resize: "vertical",
            }}
          />

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 20,
            }}
          >
            <button
              onClick={onConfirm}
              disabled={resolving}
              style={{
                order: isMobile ? 1 : 2,
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? 44 : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: "#dc2626",
                fontSize: 14,
                fontWeight: 600,
                color: "#ffffff",
                cursor: resolving ? "not-allowed" : "pointer",
                opacity: resolving ? 0.7 : 1,
              }}
            >
              {resolving ? "Rejecting..." : "Reject Request"}
            </button>
            <button
              onClick={onClose}
              disabled={resolving}
              style={{
                order: isMobile ? 2 : 1,
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? 44 : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                fontSize: 14,
                fontWeight: 600,
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
